// v3.21-1812: Settled This Month — itemized P&L-friendly admin report.
// Predicate is SETTLEMENT (all_parties_settled_timestamp).  Funded Amount
// column comes from services.funding_ssot.authoritative_funded_amount so
// Deals that settled without lender-funding evidence correctly show $0.
// API path uses the canonical /admin/settled-this-month; the legacy
// /admin/funded-this-month path remains as a backend alias for now.
// (which uses the SAME funded_payment_filter as is_deal_funded). Includes a
// one-click CSV export. Joins recent system_audit (mark-paid) records so each row
// shows whether it was reconciled and by whom.
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Loader2, RefreshCw, DollarSign, Download, ExternalLink, Search } from 'lucide-react';
import { toast } from 'sonner';
import { formatET } from '../utils/timeUtils';

const fmtMoney = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const PAYMENT_DATE_FIELDS = [
  'ptc_paid_date',
  'vendor_payment_received_date',
  'ptc_payment_received_date',
  'partner_paid_date',
  'funded_date',
];

// Derive the canonical "paid date" (first non-empty payment marker date) for sort + display.
const getPaidDate = (row) => {
  for (const f of PAYMENT_DATE_FIELDS) {
    const v = row?.[f];
    if (v && String(v).trim()) return String(v);
  }
  return '';
};

// v3.18-1288 PAYMENT-008B Slice 3 — the backend now stamps the
// canonical funding_sources[]-derived amount on each item as
// `amount_canonical`.  Prefer it; fall back to the legacy chain only
// when the backend didn't supply the canonical value (rows created by
// older cached responses).
const getRowAmount = (row) =>
  row?.amount_canonical
    ?? row?.funded_amount
    ?? row?.approved_amount
    ?? row?.deal_amount
    ?? 0;

// Build a "method" label for the report based on which markers are set.
const getMethod = (row) => {
  if (row?.invoice_status === 'paid') return 'invoice';
  if (row?.ach_confirmation_number || row?.is_ach_deal === true) return 'ach';
  if (row?.payment_confirmed) return 'manual';
  return 'manual';
};

export default function FundedThisMonthPage() {
  const [data, setData] = useState({ items: [], month: '', count: 0, total_amount: 0 });
  const [recon, setRecon] = useState({});  // deal_id -> last mark-paid audit
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [list, recents] = await Promise.all([
        api.get('/admin/settled-this-month'),
        api.get('/admin/funded-mismatches/recent-mark-paid?limit=200').catch(() => ({ data: { items: [] } })),
      ]);
      setData(list.data || { items: [], month: '', count: 0, total_amount: 0 });
      const map = {};
      (recents.data?.items || []).forEach((r) => {
        if (r.deal_id && !map[r.deal_id]) map[r.deal_id] = r;
      });
      setRecon(map);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load Settled This Month');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const rows = useMemo(() => {
    const list = (data.items || []).slice();
    list.sort((a, b) => (getPaidDate(b) || '').localeCompare(getPaidDate(a) || ''));
    if (!filter.trim()) return list;
    const q = filter.trim().toLowerCase();
    return list.filter((r) =>
      (r.full_name || '').toLowerCase().includes(q) ||
      (r.id || '').toLowerCase().includes(q) ||
      (r.sales_rep_name || '').toLowerCase().includes(q) ||
      (r.ptc_rep_name || '').toLowerCase().includes(q)
    );
  }, [data, filter]);

  const filteredTotal = useMemo(
    () => rows.reduce((s, r) => s + Number(getRowAmount(r) || 0), 0),
    [rows]
  );

  const exportCsv = () => {
    if (!rows.length) {
      toast.error('Nothing to export');
      return;
    }
    const headers = [
      'Deal Name', 'Deal ID', 'Paid Date', 'Method',
      'Amount', 'Stage', 'Funding Status',
      'Sales Rep', 'PTC Rep',
      'PTC Paid Date', 'Vendor Payment Received', 'PTC Payment Received', 'Partner Paid Date',
      'PTC Payment Status', 'Settlement Status', 'Invoice Status',
      'Reconciled By', 'Reconciled At', 'Reconciliation Method', 'ACH Confirmation #',
    ];
    const escape = (v) => {
      if (v === undefined || v === null) return '';
      const s = String(v);
      if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return '"' + s.replace(/"/g, '""') + '"';
      }
      return s;
    };
    const lines = [headers.join(',')];
    rows.forEach((r) => {
      const a = recon[r.id] || {};
      lines.push([
        r.full_name, r.id, getPaidDate(r), getMethod(r),
        getRowAmount(r), r.deal_stage, r.funding_status,
        r.sales_rep_name, r.ptc_rep_name,
        r.ptc_paid_date, r.vendor_payment_received_date, r.ptc_payment_received_date, r.partner_paid_date,
        r.ptc_payment_status, r.settlement_status, r.invoice_status,
        a.edited_by || '', a.timestamp || '', a.method || '', a.ach_confirmation_number || '',
      ].map(escape).join(','));
    });
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const slug = (data.month || 'settled-this-month').toLowerCase().replace(/\s+/g, '-');
    link.download = `funded-${slug}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} row${rows.length === 1 ? '' : 's'}`);
  };

  return (
    <div className="space-y-4 p-6" data-testid="settled-this-month-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-primary flex items-center gap-2">
            <DollarSign className="h-6 w-6 text-emerald-600" />
            Settled This Month
            {data.month && <span className="text-sm font-normal text-slate-500">— {data.month}</span>}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Every deal counted as All-Parties-Settled during the current month.
            Predicate is settlement (<code>all_parties_settled_timestamp</code>);
            the row-level Funded Amount uses <code>services.funding_ssot.authoritative_funded_amount</code>
            so deals that settled without lender-funding evidence show $0.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={fetchAll} variant="outline" disabled={loading} data-testid="refresh-funded-month-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
          <Button onClick={exportCsv} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="export-funded-month-csv">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Totals row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-3">
            <p className="text-[11px] uppercase text-slate-500 tracking-wider">Deals Counted</p>
            <p className="text-2xl font-semibold text-slate-800 mt-1" data-testid="month-deals-count">{data.count || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-3">
            <p className="text-[11px] uppercase text-slate-500 tracking-wider">Total Funded</p>
            <p className="text-2xl font-semibold text-emerald-700 mt-1" data-testid="month-total-amount">{fmtMoney(data.total_amount)}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-slate-300">
          <CardContent className="p-3">
            <p className="text-[11px] uppercase text-slate-500 tracking-wider">Filtered View</p>
            <p className="text-2xl font-semibold text-slate-800 mt-1">{rows.length} • {fmtMoney(filteredTotal)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="relative max-w-md">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by name, deal ID, or rep…"
          className="pl-9"
          data-testid="funded-month-filter"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-sm text-slate-500">No funded deals match your filter.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50/70">
                  <tr className="border-b border-slate-200">
                    {['Deal', 'Paid Date', 'Method', 'Amount', 'Stage', 'Rep', 'Reconciled', ''].map((h) => (
                      <th key={h} className="text-left py-2 px-3 text-[11px] font-medium text-slate-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const a = recon[r.id];
                    const method = getMethod(r);
                    return (
                      <tr key={r.id} className="border-b border-slate-100 hover:bg-emerald-50/40" data-testid={`funded-row-${r.id}`}>
                        <td className="py-2 px-3">
                          <div className="font-medium text-slate-800">{r.full_name || '—'}</div>
                          <div className="text-[10px] font-mono text-slate-400">{r.id}</div>
                        </td>
                        <td className="py-2 px-3 text-xs text-slate-600">{getPaidDate(r) || '—'}</td>
                        <td className="py-2 px-3">
                          <Badge variant="outline" className={
                            method === 'ach' ? 'border-blue-200 text-blue-700 bg-blue-50' :
                            method === 'invoice' ? 'border-purple-200 text-purple-700 bg-purple-50' :
                            'border-slate-200 text-slate-600 bg-slate-50'
                          }>{method}</Badge>
                        </td>
                        <td className="py-2 px-3 font-semibold text-emerald-700">{fmtMoney(getRowAmount(r))}</td>
                        <td className="py-2 px-3"><Badge variant="outline" className="text-xs">{r.deal_stage || '—'}</Badge></td>
                        <td className="py-2 px-3 text-xs text-slate-600">{r.sales_rep_name || r.ptc_rep_name || '—'}</td>
                        <td className="py-2 px-3 text-xs">
                          {a ? (
                            <span className="text-amber-700" title={`Method: ${a.method}${a.ach_confirmation_number ? ' #' + a.ach_confirmation_number : ''}`}>
                              {a.edited_by} <span className="text-slate-400">• {a.timestamp ? formatET(a.timestamp, 'M/d/yy h:mm a') : ''}</span>
                            </span>
                          ) : (
                            <span className="text-slate-400">organic</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right">
                          <Link
                            to={`/clients/${r.id}`}
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-xs"
                            data-testid={`open-funded-${r.id}`}
                          >
                            Open <ExternalLink className="h-3 w-3" />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
